import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Button, Callout, Card, Heading, Icon, PageHead, Text } from '../ui/index.js';
import {
  PLANS,
  formatPrice,
  type PlanDefinition,
  type PlanInterval,
  type PlanTier,
  type PurchasablePlanKey,
} from '@goodbuild/shared';
import { api } from '../api/client.js';
import { capabilities } from '../platform/index.js';
import { openWebHandoff } from '../auth/session.js';
import { useAccess, useAwaitAccess, useMe } from '../api/hooks.js';
import { errorMessage } from '../lib/errors.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { readAffiliate } from '../lib/settings.js';
import {
  ANNUAL_SAVING_PERCENT,
  monthlyEquivalentCents,
  PLAN_TIER_LABELS,
} from '../lib/planLabels.js';
import { Loading } from '../components/layout/Loading.js';
import { SUPPORTER_BENEFITS } from '../components/layout/SupporterCta.js';

export const Route = createFileRoute('/join')({
  component: Join,
  validateSearch: (
    search: Record<string, unknown>,
  ): { plan?: PurchasablePlanKey; status?: string } => ({
    ...(typeof search.plan === 'string' ? { plan: search.plan as PurchasablePlanKey } : {}),
    ...(typeof search.status === 'string' ? { status: search.status } : {}),
  }),
});

type Step = 'choose' | 'confirming' | 'done' | 'slow';

const BILLING_OPTIONS: ReadonlyArray<{ interval: PlanInterval; label: string }> = [
  { interval: 'month', label: 'Monthly' },
  { interval: 'year', label: 'Annual' },
];

interface Tier {
  tier: PlanTier;
  /** One line under the tier name, saying who it is for. */
  tagline: string;
  features: readonly string[];
  /** The column the table leans on. There is one, and it is the one most people want. */
  featured?: boolean;
}

/**
 * What is on offer, as a column.
 *
 * Supporter's list is `SUPPORTER_BENEFITS` itself rather than a copy, because the slab sells
 * the same membership and the first edit to either would otherwise split them.
 */
const TIERS: readonly Tier[] = [
  {
    tier: 'supporter',
    tagline: 'Everything we make, while we make it.',
    features: SUPPORTER_BENEFITS,
    featured: true,
  },
];

function planFor(tier: PlanTier, interval: PlanInterval): PlanDefinition {
  return PLANS[`${tier}_${interval}` as PurchasablePlanKey];
}

/**
 * Joining (`03-client-app.md` section 4.1).
 *
 * Prices are VAT inclusive, so what is on the card is what would be charged and nothing is
 * added on the way out.
 *
 * Nothing is on sale. There is no payment provider connected, so this screen is the price list
 * and the sign in door, and it says so rather than offering a button that cannot charge. The
 * checkout, the withdrawal waiver and the return-from-payment handling all went with the old
 * provider; they belong back here when a new one is wired up.
 *
 * While the launch offer is running the cards price themselves with it: the discounted figure
 * is the headline and the list price is struck through beside it.
 *
 * The prices are public. Somebody deciding whether GOOD BUILD is worth eight euros a month
 * should not have to make an account to find out what it costs, so this screen renders the plan
 * picker for a visitor too.
 */
function Join() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const me = useMe();
  const { signedIn, hasAccess } = useAccess();

  const [planKey, setPlanKey] = React.useState<PurchasablePlanKey | null>(search.plan ?? null);
  /**
   * Which column of prices the table is showing. A `?plan=` in the URL has already decided it,
   * so arriving on a link to the yearly Crew plan shows the yearly prices rather than monthly
   * ones with the wrong plan quietly selected.
   */
  const [billing, setBilling] = React.useState<PlanInterval>(
    search.plan ? PLANS[search.plan].interval : 'month',
  );
  /**
   * A return from the checkout, decided once on arrival.
   *
   * Read from the URL rather than from state, because this is the first render after a full
   * page load: the browser has been away at Creem and everything in memory went with it.
   */
  const returning = search.status === 'done';

  const [step, setStep] = React.useState<Step>(returning ? 'confirming' : 'choose');
  const [error, setError] = React.useState<string | null>(null);

  const affiliate = readAffiliate();

  /**
   * Access landing is the answer to everything, whichever screen is showing.
   *
   * It covers the ordinary case (the webhook arrived while they were being redirected) and the
   * slow one (it arrived after we had already given up and said so), so a late webhook still
   * turns into a welcome rather than leaving somebody on an apology.
   */
  React.useEffect(() => {
    if (hasAccess) setStep('done');
  }, [hasAccess]);

  // Only polls while a signed-in account has a payment outstanding. `hasAccess` ends it by
  // flipping the step above, which stops this on the next render. A signed-out visitor who
  // arrives on a stray `?status=done` has nothing to wait for and must not poll for it.
  const { givenUp } = useAwaitAccess(signedIn && step === 'confirming');

  React.useEffect(() => {
    if (givenUp && step === 'confirming') setStep('slow');
  }, [givenUp, step]);

  /**
   * Drops `?status=` once it has been acted on.
   *
   * Left in the address bar, a refresh would put the screen back into `confirming` over a
   * payment that resolved long ago -- which is the stuck-screen bug the old flow had, arriving
   * by a different route.
   */
  React.useEffect(() => {
    if (!returning) return;
    if (signedIn && step === 'confirming') return;
    void navigate({
      to: '/join',
      search: (prev) => ({ ...prev, status: undefined }),
      replace: true,
    });
  }, [returning, step, signedIn, navigate]);

  const [busy, setBusy] = React.useState(false);

  const payLabel = !signedIn
    ? 'Sign in to continue'
    : busy
      ? 'Opening checkout'
      : capabilities.hostedCheckout
        ? 'Continue to payment'
        : 'Continue in your browser';

  /**
   * Flipping the switch keeps the tier already chosen and only changes how often it is billed.
   * Clearing the selection instead would make the switch feel like it had undone something.
   */
  const chooseBilling = (interval: PlanInterval) => {
    setBilling(interval);
    setPlanKey((current) => (current ? planFor(PLANS[current].tier, interval).key : current));
  };

  /**
   * Sign in, hand off, or open a checkout.
   *
   * Prices are public and paying is not, so a visitor is sent to sign in first and comes back.
   *
   * The apps never open a checkout themselves. Payment always happens on goodbuild.cc
   * (`01-product-rules.md` section 10), so they hand off to the web app already signed in and
   * let the browser do it. That is a product rule, but it is also what keeps the desktop and
   * Android builds working at all: their webview has a content security policy that does not
   * allow the payment provider, and navigating there directly is simply blocked.
   *
   * Access is not granted on return either way. The webhook does that, because a browser
   * coming back proves somebody closed a tab, not that a payment cleared.
   */
  const start = async () => {
    if (!signedIn) {
      void navigate({ to: '/signin', search: { return_to: '/join' } });
      return;
    }
    if (!planKey || busy) return;

    setBusy(true);
    setError(null);
    try {
      if (!capabilities.hostedCheckout) {
        // The plan travels in the URL so the web app opens on the same choice rather than the
        // picker, which would make somebody choose twice.
        await openWebHandoff(`/join?plan=${planKey}`);
        setBusy(false);
        return;
      }

      const result = await api.post<{ checkoutId: string; purchaseUrl: string | null }>(
        '/checkout',
        { planKey },
      );
      if (!result.purchaseUrl) {
        setError('Checkout is not available right now. Please try again in a moment.');
        setBusy(false);
        return;
      }
      window.location.assign(result.purchaseUrl);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  // Until `/me` answers we do not know whether this is a visitor or somebody coming back from
  // a checkout, and those two get different screens. Waiting beats flashing the wrong one.
  if (me.isLoading) return <Loading />;

  // Everything below belongs to an account: a visitor always gets the plan picker.
  if (signedIn && step === 'confirming') {
    return <Loading label="Confirming your payment" />;
  }

  /**
   * The payment went through and Access has not appeared. Saying so beats a spinner: the money
   * has moved either way, and the one thing they must not be told is to pay again.
   */
  if (signedIn && step === 'slow') {
    return (
      <div className="gb-center">
        <Heading size="5">Your payment went through. Access is taking a moment.</Heading>
        <Text render={<p />} size="2" color="gray">
          Nothing else needs paying. This usually settles within a minute, and we will notify you
          the moment it does.
        </Text>
        <div className="gb-row gb-row--tight">
          <Focusable autoFocus onSelect={() => setStep('confirming')} selectLabel="Check again">
            <Button size="3">Check again</Button>
          </Focusable>
          <Focusable onSelect={() => void navigate({ to: '/games' })} selectLabel="Browse games">
            <Button size="3" variant="surface">
              Browse games
            </Button>
          </Focusable>
        </div>
      </div>
    );
  }

  if (signedIn && step === 'done') {
    return (
      <div className="gb-center">
        <Heading size="6">You are in. Welcome to GOOD BUILD.</Heading>
        <Focusable
          autoFocus
          onSelect={() => void navigate({ to: '/games' })}
          selectLabel="Start playing"
        >
          <Button size="4">Start playing</Button>
        </Focusable>
      </div>
    );
  }

  return (
    <FocusZone focusKey="join" direction="column" className="gb-stack">
      <PageHead
        eyebrow="Membership"
        title="Become a supporter."
        description="Everything we make, while we make it. Cancel any time."
      />
      <Text size="2" color="gray">
        {'Prices include VAT. What you see is what you pay.'}
      </Text>
      {affiliate ? (
        <Text size="2" color="gray">
          {`Referred by ${affiliate}`}
        </Text>
      ) : null}

      {/* The billing switch sits above the columns, because it changes every price in them. */}
      <FocusZone
        focusKey="join-billing"
        direction="row"
        className="gb-row gb-row--between gb-pricing__switch"
      >
        <div className="gb-segmented" role="group" aria-label="Billing period">
          {BILLING_OPTIONS.map((option) => (
            <Focusable
              key={option.interval}
              onSelect={() => chooseBilling(option.interval)}
              selectLabel={option.label}
              className="gb-focusable--flat"
            >
              {/* The click is handled by the focus wrapper, which every interactive thing in
                  the app goes through; a second handler here would fire it twice. */}
              <button
                type="button"
                className="gb-segmented__item"
                aria-pressed={billing === option.interval}
              >
                {option.label}
              </button>
            </Focusable>
          ))}
        </div>

        <Badge color="green">{`Annual is ${ANNUAL_SAVING_PERCENT}% less than monthly`}</Badge>
      </FocusZone>

      <div className="gb-pricing">
        {TIERS.map(({ tier, tagline, features, featured }) => {
          const plan = planFor(tier, billing);
          const selected = planKey === plan.key;
          const tierLabel = PLAN_TIER_LABELS[tier];
          // What this card is selling for today. The discount comes off the amount actually
          // charged and the per-month figure is worked out from that, rather than the other way
          // round: dividing first and discounting after can land a cent away from the invoice.

          return (
            <Focusable
              key={tier}
              autoFocus={tier === (planKey ? PLANS[planKey].tier : 'supporter')}
              onSelect={() => setPlanKey(plan.key)}
              selectLabel={selected ? 'Selected' : `Choose ${tierLabel}`}
            >
              <Card
                size="4"
                variant={selected ? 'surface' : 'ghost'}
                className="gb-pricing__card"
                data-featured={featured ? 'true' : undefined}
                data-selected={selected ? 'true' : undefined}
              >
                {/* `featured` still styles the column it leans on, but it no longer prints
                    "Most popular" over it. With one tier on offer that badge is a claim about
                    what other people chose, out of a set of one, and there is no figure behind
                    it. It comes back when there are two columns and something to compare. */}
                <div className="gb-row gb-row--between">
                  <Heading size="4">{tierLabel}</Heading>
                </div>

                <Text render={<p />} size="2" color="gray">
                  {tagline}
                </Text>

                <p className="gb-pricing__price">
                  <span className="gb-pricing__amount">
                    {formatPrice(
                      monthlyEquivalentCents(plan.priceCents, plan.interval),
                      plan.currency,
                    )}
                  </span>
                  <span className="gb-pricing__period">{'/ month'}</span>
                </p>

                <Text size="1" color="gray">
                  {billing === 'year'
                    ? `${formatPrice(plan.priceCents, plan.currency)}, billed once a year.`
                    : 'Billed monthly. Cancel any time.'}
                </Text>

                <ul className="gb-checklist gb-pricing__features">
                  {features.map((feature) => (
                    <li key={feature}>
                      <Icon name="check" size={16} />
                      <Text size="2">{feature}</Text>
                    </li>
                  ))}
                </ul>

                {/* Selecting is all this does. Paying needs the waiver below it, so the one
                    action that leaves this screen stays at the bottom of the screen. */}
                <Button size="3" block variant={selected ? 'solid' : 'surface'}>
                  {selected ? 'Selected' : `Choose ${tierLabel}`}
                </Button>
              </Card>
            </Focusable>
          );
        })}
      </div>

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      <Text size="2" color="gray">
        {!signedIn
          ? 'Sign in with Discord first. Nothing is charged until you confirm at the checkout.'
          : 'Payment is handled by Creem, our payment provider. We take you there and back again.'}
      </Text>

      <Focusable
        onSelect={() => void start()}
        selectLabel={payLabel}
        disabled={signedIn && (!planKey || busy)}
      >
        <Button size="4" disabled={signedIn && (!planKey || busy)}>
          {payLabel}
        </Button>
      </Focusable>

      {/* Where the terms are, since the cards above refer to them. */}
      <div className="gb-row gb-row--tight">
        <Focusable
          onSelect={() => void navigate({ to: '/terms' })}
          selectLabel="Terms of service"
          className="gb-focusable--flat"
        >
          <Button size="1" variant="ghost">
            {'Terms of service'}
          </Button>
        </Focusable>
        <Focusable
          onSelect={() => void navigate({ to: '/privacy' })}
          selectLabel="Privacy policy"
          className="gb-focusable--flat"
        >
          <Button size="1" variant="ghost">
            {'Privacy policy'}
          </Button>
        </Focusable>
      </div>
    </FocusZone>
  );
}
